import { Octokit } from '@octokit/core';
import { Inject, Injectable } from '@rxdi/core';
import { gql, HttpClient } from '@rxdi/http';
import { map } from 'rxjs/operators';

import { LocalStorageService } from '../local-storage/local-storage.service';
import { Mutation, Query } from './github.interface';
const REPOSITORY_FRAGMENT = gql`
  name
  url
  sshUrl
  description
  owner {
    id
    login
    avatarUrl
    url
  }
`;

const RATE_LIMIT_FRAGMENT = gql`
  cost
  limit
  nodeCount
  remaining
  resetAt
  used
`;

const VIEWER_FRAGMENT = gql`
  id
  avatarUrl
  login
  url
`;

@Injectable()
export class GithubService {
  @Inject(LocalStorageService)
  private localStorage: LocalStorageService;

  @Inject(HttpClient)
  private http: HttpClient;

  getAccessToken() {
    return this.localStorage.get('github-accessToken');
  }

  setAccessToken(token: string) {
    return this.localStorage.update('github-accessToken', token);
  }

  setGithubUserName(name: string) {
    return this.localStorage.update('github-username', name);
  }

  getGithubUserName() {
    return this.localStorage.get<string>('github-username');
  }
  findRepository(name: string) {
    return this.query(
      gql`
    query findRepository {
      viewer {
        repository(name:"${name}") {
          ${REPOSITORY_FRAGMENT}
        }
      }
      rateLimit {
        ${RATE_LIMIT_FRAGMENT}
      }
    }
    `
    ).pipe(map((res) => res.data.viewer.repository));
  }

  private query<T = Record<string, string>, K = Record<string, string>>(query, variables?: K) {
    return this.http.query<{ data: Query & Mutation }, K>(query, variables, {
      headers: this.getHeaders() as never,
      method: 'POST',
    });
  }

  private getHeaders() {
    return {
      Authorization: ['bearer', this.getAccessToken()].join(' '),
    } as never;
  }

  deleteProject(repo: string, owner?: string) {
    return new Octokit({ auth: this.getAccessToken() }).request('DELETE /repos/{owner}/{repo}', {
      owner: owner || this.getGithubUserName(),
      repo,
    });
  }

  createRepository({ description, name }: { name: string; description: string }) {
    return this.query(
      gql`
   mutation createRepository {
    createRepository(input: { name: "${name}", visibility: PRIVATE, description: "${description}" }) {
     clientMutationId
     repository {
      ${REPOSITORY_FRAGMENT}
     }
    }
   }
  `
    ).pipe(map((res) => res.data.createRepository.repository));
  }

  getViewer() {
    return this.query(
      gql`
   query getViewer {
    viewer {
     ${VIEWER_FRAGMENT}
    }
    rateLimit {
     ${RATE_LIMIT_FRAGMENT}
    }
   }
  `
    ).pipe(map((res) => res.data.viewer));
  }
}
